import React from 'react';
import PhoneInput from 'react-phone-number-input';
import flags from 'react-phone-number-input/flags';
import 'react-phone-number-input/style.css';
import styles from './PhoneField.module.scss';

export interface PhoneFieldProps {
  /** Forwarded to the inner <input> so an external <label htmlFor> binds. */
  id?: string;
  /** Current E.164 value (or undefined when empty). */
  value?: string;
  /** Receives the E.164 value, or undefined when cleared. */
  onChange: (value?: string) => void;
  /** Inline error message; also turns the box red. */
  error?: string;
  /** Lets a page tune sizing to its neighbouring inputs (CSS vars). */
  className?: string;
}

/**
 * Country-dropdown phone input that emits E.164. Flags are bundled locally
 * (no external CDN). Re-skinned to the Job Beast input tokens in the SCSS.
 */
const PhoneField: React.FC<PhoneFieldProps> = ({ id, value, onChange, error, className }) => {
  const rootClass = [styles.field, error ? styles.invalid : '', className]
    .filter(Boolean)
    .join(' ');
  const errorId = id ? `${id}-error` : undefined;

  return (
    <div className={rootClass}>
      <PhoneInput
        international
        defaultCountry="US"
        // Lock the "+<calling code>" prefix so it can't be deleted by typing.
        // The country is changed via the dropdown, not by editing the code.
        // This guarantees a typed national number (e.g. (201) 555-0123) always
        // resolves to E.164 (+1…) and handles the focus-select-and-retype
        // browser quirk; without it, deleting the prefix could yield a bad value.
        countryCallingCodeEditable={false}
        flags={flags}
        value={value}
        onChange={onChange}
        numberInputProps={{
          id,
          name: 'phone',
          'aria-invalid': error ? true : undefined,
          'aria-describedby': error ? errorId : undefined,
        }}
      />
      {error && (
        <small id={errorId} role="alert" className={styles.error}>
          <span aria-hidden="true">⚠</span> {error}
        </small>
      )}
    </div>
  );
};

export default PhoneField;
